"use client";

/**
 * SetupPage Component
 *
 * Main orchestrator for the onboarding setup flow.
 * Coordinates between all step components and manages the overall flow.
 *
 * Flow for logged-out users:
 * 0. UsernameStep - Enter Instagram/TikTok username
 *    → LoadingOverlay (intermediary, full-screen)
 * 1. AnalysisStep - AI analysis of account (data pre-loaded)
 * 2. AccountStep - Create Outfame account (email/password)
 * 3. PaymentStep - Select plan and complete payment
 *
 * Flow for logged-in users (adding new account):
 * 0. UsernameStep
 *    → LoadingOverlay
 * 1. AnalysisStep
 * 3. PaymentStep (skip account creation)
 */
import { useCallback, useState, useEffect, useRef } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import dynamic from "next/dynamic";
import SetupLayout from "./components/SetupLayout";
import BlurTransition from "./components/BlurTransition";
import LoadingOverlay from "./components/LoadingOverlay";

// Step components - UsernameStep loaded immediately (step 0)
import UsernameStep from "./components/steps/UsernameStep";

// Lazy load other steps - reduces initial bundle by ~35KB
const AnalysisStep = dynamic(() => import("./components/steps/AnalysisStep"), {
  loading: () => null
});
const AccountStep = dynamic(() => import("./components/steps/AccountStep"), {
  loading: () => null
});
const PaymentStep = dynamic(() => import("./components/steps/PaymentStep"), {
  ssr: false,
  // Has Evervault client-only code
  loading: () => null
});
const SuccessStep = dynamic(() => import("./components/steps/SuccessStep"), {
  loading: () => null
});

// Hooks
import { useSetupFlow } from "./hooks/useSetupFlow";
import { useSignInWithProvider } from "@/utils/react-query-hooks";
import { useSetupSession } from "./hooks/useSetupSession";
import { useAbandonmentTracking } from "./hooks/useAbandonmentTracking";
import { getAllAccountsForUser } from "@/utils/supabase-queries";
import supabaseClient from "@/utils/supabase-browser";
import type { ProfileData, AIAnalysis } from "./types";

// localStorage key for preserving setup state across OAuth redirect
const SETUP_STATE_KEY = "outfame-setup-state";
// Cookie name for server-side detection of setup-in-progress
const SETUP_COOKIE_NAME = "outfame-setup-in-progress";

// Animation timing constants
const EXIT_ANIMATION_DURATION = 250; // ms - matches BlurTransition exit

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}
export default function SetupPage() {
  const router = useRouter();

  // Wallet-forward checkout flag (`wallet_forward_checkout_v1`). Fetched at
  // RUNTIME on the client — the setup route is statically rendered, so a
  // server-read prop would freeze the flag at build time and never flip until
  // redeploy. Defaults false until the fetch resolves, so the load window
  // renders the exact flag-OFF arm (correct-by-construction).
  const [walletForwardCheckout, setWalletForwardCheckout] = useState(false);
  useEffect(() => {
    let cancelled = false;
    fetch("/api/feature-flags/wallet-forward-checkout").then(r => r.ok ? r.json() : null).then(d => {
      if (!cancelled && d && typeof d.enabled === "boolean") setWalletForwardCheckout(d.enabled);
    }).catch(() => {
      // Fail-quiet to the OFF arm — never break setup on a flag read error.
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Plan-screen simplified flag (`plan_screen_simplified_v1`). Fetched at
  // RUNTIME (same reason as wallet-forward above — the setup route is static,
  // so a server prop would freeze the flag until redeploy). Defaults false
  // until the fetch resolves, so the load window renders the exact flag-OFF arm.
  const [planScreenSimplified, setPlanScreenSimplified] = useState(false);
  useEffect(() => {
    let cancelled_0 = false;
    fetch("/api/feature-flags/plan-screen-simplified").then(r_0 => r_0.ok ? r_0.json() : null).then(d_0 => {
      if (!cancelled_0 && d_0 && typeof d_0.enabled === "boolean") setPlanScreenSimplified(d_0.enabled);
    }).catch(() => {
      // Fail-quiet to the OFF arm — never break setup on a flag read error.
    });
    return () => {
      cancelled_0 = true;
    };
  }, []);
  const searchParams = useSearchParams();
  const flow = useSetupFlow();

  // Session tracking
  const {
    sessionId,
    trackStep,
    trackUsername,
    trackPaymentFormEnter,
    trackPaymentSubmit,
    linkToUser,
    isInitialized: isSetupSessionInitialized
  } = useSetupSession();
  const {
    stopTracking
  } = useAbandonmentTracking();

  // Offer param (ads funnel variants)
  const offerParam = searchParams.get("offer");
  const [offerCookie, setOfferCookie] = useState<string | null>(null);
  useEffect(() => {
    setOfferCookie(readCookie("offer"));
  }, []);
  const offer = offerParam || offerCookie;

  // Success step state — shown after payment/trial completion
  const [successAccountId, setSuccessAccountId] = useState<string | null>(null);

  // Loading overlay state - shown during data fetching after username submit
  // Also shown during recovery (when page refreshed with profile data missing)
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);

  // Existing usernames for duplicate detection (logged-in users only)
  const [existingUsernames, setExistingUsernames] = useState<Set<string>>(new Set());

  // Fetch existing accounts for logged-in users to enable duplicate detection
  useEffect(() => {
    if (flow.isLoggedIn && flow.authChecked) {
      getAllAccountsForUser(supabaseClient).then(accounts => {
        const usernames = new Set(accounts.map(acc => acc.username?.toLowerCase()).filter(Boolean) as string[]);
        setExistingUsernames(usernames);
      }).catch(() => {
        // Silently fail - duplicate check is a nice-to-have
      });
    }
  }, [flow.isLoggedIn, flow.authChecked]);

  // Auto-trigger recovery when needed
  // This happens when user lands on step > 0 but profile data is missing
  useEffect(() => {
    if (flow.needsRecovery && flow.username && !showLoadingOverlay) {
      console.log("[SetupPage] Triggering recovery fetch for:", flow.username);
      setShowLoadingOverlay(true);
      flow.actions.clearNeedsRecovery();
    }
  }, [flow.needsRecovery, flow.username, flow.actions, showLoadingOverlay]);

  // Track step changes
  useEffect(() => {
    trackStep(flow.currentStep);
  }, [flow.currentStep, trackStep]);

  // Link setup session to authenticated user (covers OAuth + existing users)
  const hasLinkedSessionRef = useRef(false);
  useEffect(() => {
    if (!isSetupSessionInitialized) return;
    if (!flow.isLoggedIn) return;
    if (hasLinkedSessionRef.current) return;
    hasLinkedSessionRef.current = true;
    linkToUser().catch(() => {
      // Silent fail
    });
  }, [flow.isLoggedIn, linkToUser, isSetupSessionInitialized]);

  // Track PaymentStep's internal sub-page for customizing header
  const [paymentSubPage, setPaymentSubPage] = useState<"plan-selection" | "payment">("plan-selection");
  const [triggerBackToPlanSelection, setTriggerBackToPlanSelection] = useState(false);

  // Layout state - DELAYED to prevent mid-transition layout breakage
  // When transitioning TO payment: wait for exit animation, then expand
  // When transitioning FROM payment: wait for exit animation, then collapse
  const isPaymentStep = flow.currentStep === 3;
  const [layoutFullWidth, setLayoutFullWidth] = useState(isPaymentStep);
  const layoutTimerRef = useRef<NodeJS.Timeout | null>(null);

  // OAuth state (supports Google, Facebook, etc.)
  const [oAuthLoadingProvider, setOAuthLoadingProvider] = useState<string | null>(null);
  const oAuthMutation = useSignInWithProvider();

  // Sync layout with step changes, with delay for smooth transitions
  useEffect(() => {
    // Clear any pending layout change
    if (layoutTimerRef.current) {
      clearTimeout(layoutTimerRef.current);
    }
    if (isPaymentStep !== layoutFullWidth) {
      // Delay layout change until exit animation completes
      // This ensures old content exits at OLD layout, new content enters at NEW layout
      layoutTimerRef.current = setTimeout(() => {
        setLayoutFullWidth(isPaymentStep);
      }, EXIT_ANIMATION_DURATION);
    }
    return () => {
      if (layoutTimerRef.current) {
        clearTimeout(layoutTimerRef.current);
      }
    };
  }, [isPaymentStep, layoutFullWidth]);

  // Scroll to top on step changes (mobile UX improvement)
  // Use 300ms smooth scroll matching step transition timing
  const prevStepRef = useRef(flow.currentStep);
  useEffect(() => {
    if (prevStepRef.current !== flow.currentStep) {
      // Scroll to top with smooth animation
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      prevStepRef.current = flow.currentStep;
    }
  }, [flow.currentStep]);

  // Predictive prefetching - load next step components before user navigates
  // This ensures instant transitions between steps
  useEffect(() => {
    if (flow.currentStep === 0) {
      // User on username step - prefetch analysis step
      import("./components/steps/AnalysisStep");
    } else if (flow.currentStep === 1) {
      // User on analysis step - prefetch account and payment steps
      import("./components/steps/AccountStep");
      import("./components/steps/PaymentStep");
    }
  }, [flow.currentStep]);

  // Handle OAuth return: clean up URL params once profile + auth are ready
  const hasHandledOAuthReturnRef = useRef(false);
  useEffect(() => {
    // Skip if already handled
    if (hasHandledOAuthReturnRef.current) return;
    const searchParams_0 = new URLSearchParams(window.location.search);
    const fromOAuth = searchParams_0.get("fromOAuth");

    // Only handle if this is an OAuth return
    if (fromOAuth !== "true") return;

    // Wait for profile data to be restored AND user to be authenticated
    if (!flow.profileData || !flow.isLoggedIn) {
      console.log("[SetupPage] OAuth return: Waiting for profile data or auth state...");
      return;
    }

    // Mark as handled to prevent duplicate execution
    hasHandledOAuthReturnRef.current = true;
    console.log("[SetupPage] OAuth return: Profile and auth ready");

    // Clean up URL params
    const params = new URLSearchParams(searchParams_0.toString());
    params.delete("fromOAuth");
    params.delete("createTrial");
    const cleanUrl = params.toString() ? `${window.location.pathname}?${params.toString()}` : window.location.pathname;
    router.replace(cleanUrl, {
      scroll: false
    });
  }, [flow.profileData, flow.isLoggedIn, router]);

  // Calculate total steps based on login status
  // Logged in: 3 steps (0, 1, 3) - skip step 2 (account creation)
  // Logged out: 4 steps (0, 1, 2, 3) - all steps
  const totalSteps = flow.isLoggedIn ? 3 : 4;

  // Handle username step submission - shows loading overlay
  const handleUsernameSubmit = useCallback(() => {
    flow.actions.setError(null);
    setShowLoadingOverlay(true);
  }, [flow.actions]);

  // Handle loading overlay completion - receives fetched data
  // CRITICAL: This is called while overlay is STILL FULLY VISIBLE
  // Two modes:
  // 1. Normal flow (step 0): change to step 1, then hide overlay
  // 2. Recovery flow (step > 0): just update data, hide overlay (already on correct step)
  const handleLoadingComplete = useCallback((result: {
    profile: ProfileData | null;
    analysis: AIAnalysis | null;
    error: string | null;
  }) => {
    const isRecoveryMode = flow.currentStep > 0;
    if (result.error || !result.profile) {
      // Error case
      setShowLoadingOverlay(false);
      if (isRecoveryMode) {
        // During recovery: show error on current step
        flow.actions.setError(result.error || "Could not fetch account data");
      } else {
        // Normal flow: show error on step 0
        flow.actions.setError(result.error || "Could not fetch account data");
      }
      return;
    }

    // Success case - store fetched data
    flow.actions.setProfileData(result.profile);
    if (result.analysis) {
      flow.actions.setAIAnalysis(result.analysis);
    }
    if (isRecoveryMode) {
      // Recovery mode: already on correct step, just hide overlay
      requestAnimationFrame(() => {
        setShowLoadingOverlay(false);
      });
    } else {
      // Normal flow: change step to 1 (AnalysisStep) WHILE overlay is still visible
      flow.actions.goNext();

      // Hide overlay AFTER step change - it will fade out to reveal step 1
      requestAnimationFrame(() => {
        setShowLoadingOverlay(false);
      });
    }
  }, [flow.actions, flow.currentStep]);

  // Handle analysis step continue
  // Logged in: Go to PaymentStep (skip account creation)
  // Not logged in: Go to AccountStep
  const handleAnalysisContinue = useCallback(() => {
    flow.actions.goNext();
  }, [flow.actions]);

  // Handle analysis step retry
  const handleAnalysisRetry = useCallback(() => {
    flow.actions.goToStep(0);
    flow.actions.setProfileData(null as any);
    flow.actions.setAIAnalysis(null as any);
  }, [flow]);

  // Handle account creation submission (called after successful signup in AccountStep)
  // User is now logged in — proceed to payment step
  const handleAccountSubmit = useCallback(() => {
    flow.actions.goNext();
  }, [flow.actions]);

  // Handle OAuth with state preservation (Google, Facebook, etc.)
  // Saves setup state to localStorage and sets cookie for server-side redirect detection
  const handleOAuth = useCallback((provider: "google" | "facebook") => {
    setOAuthLoadingProvider(provider);

    // 1. Save full setup state to localStorage (will be restored after OAuth redirect)
    const setupState = {
      platform: flow.platform,
      username: flow.username,
      selectedAvatarUrl: flow.selectedAvatarUrl,
      profileData: flow.profileData,
      aiAnalysis: flow.aiAnalysis,
      activePeriod: flow.activePeriod
    };
    try {
      localStorage.setItem(SETUP_STATE_KEY, JSON.stringify(setupState));
    } catch (e) {
      console.error("Failed to save setup state to localStorage:", e);
    }

    // 2. Set cookie flag for server-side detection in auth callback
    // Expires in 10 minutes - should be plenty of time for OAuth flow
    document.cookie = `${SETUP_COOKIE_NAME}=true; path=/; max-age=600; SameSite=Lax`;

    // 3. Trigger OAuth - will redirect to provider, then back to /auth/callback
    // The callback will detect the cookie and redirect to /setup?step=3&fromOAuth=true
    oAuthMutation.mutate({
      provider
    });
  }, [flow, oAuthMutation]);

  // Handle payment completion
  const handlePaymentComplete = useCallback(async (paymentMethod: "card" | "wallet", accountId: string) => {
    // Show success step instead of redirecting immediately
    setSuccessAccountId(accountId);
  }, []);

  // Render current step
  const renderStep = () => {
    switch (flow.currentStep) {
      case 0:
        return <UsernameStep platform={flow.platform} username={flow.username} isLoading={flow.isLoading} error={flow.error} onPlatformChange={flow.actions.setPlatform} onUsernameChange={flow.actions.setUsername} onAvatarUrlChange={flow.actions.setSelectedAvatarUrl} onSubmit={handleUsernameSubmit} onTrackUsername={trackUsername} existingUsernames={existingUsernames} />;
      case 1:
        return <AnalysisStep platform={flow.platform} profileData={flow.profileData} aiAnalysis={flow.aiAnalysis} isLoadingProfile={false} isLoadingAnalysis={false} error={flow.error} onContinue={handleAnalysisContinue} onRetry={handleAnalysisRetry} />;
      case 2:
        // Account creation step (only for non-logged-in users)
        return <AccountStep email={flow.email} password={flow.password} isLoading={flow.isLoading} error={flow.error} onEmailChange={flow.actions.setEmail} onPasswordChange={flow.actions.setPassword} onSubmit={handleAccountSubmit} onOAuth={handleOAuth} oAuthLoadingProvider={oAuthLoadingProvider} onLinkSession={linkToUser} />;
      case 3:
        return <PaymentStep platform={flow.platform} profileData={flow.profileData} activePeriod={flow.activePeriod} onPeriodChange={flow.actions.setActivePeriod} selectedPlanName={flow.selectedPlanName} onPlanChange={flow.actions.setSelectedPlan} isLoading={flow.isLoading} error={flow.error} onComplete={handlePaymentComplete} onSubPageChange={subPage => {
          setPaymentSubPage(subPage);
          // Persist card-form sub-page entry (hook guards once per session)
          if (subPage === "payment") {
            trackPaymentFormEnter();
          }
          // Reset trigger after it's been consumed
          if (triggerBackToPlanSelection) {
            setTriggerBackToPlanSelection(false);
          }
        }} onPaymentSubmitAttempt={trackPaymentSubmit} goBackToPlanSelection={triggerBackToPlanSelection} userEmail={flow.userEmail} userId={flow.userId} offer={offer} coupon={flow.coupon} walletForwardCheckout={walletForwardCheckout} planScreenSimplified={planScreenSimplified} />;
      default:
        return null;
    }
  };

  // Payment step uses full width (no social proof panel)
  const isPaymentFormSubPage = isPaymentStep && paymentSubPage === "payment";

  // Custom back button behavior for payment form sub-page
  const handleBackClick = useCallback(() => {
    if (isPaymentFormSubPage) {
      // Go back to plan selection within PaymentStep
      setTriggerBackToPlanSelection(true);
    } else {
      // Normal back navigation
      flow.actions.goBack();
    }
  }, [isPaymentFormSubPage, flow.actions]);

  // If success step is active, render it outside the normal flow
  if (successAccountId) {
    return <>
            <SetupLayout currentStep={0} totalSteps={totalSteps} showBackButton={false} fullWidth isLoggedIn>
                    <SuccessStep onContinue={() => {
          stopTracking();
          router.push(`/app/account/${successAccountId}?upgraded=true`);
        }} />
                </SetupLayout>
            </>;
  }
  return <>
            {/* Full-screen loading overlay - shown during account analysis */}
            <LoadingOverlay isVisible={showLoadingOverlay} username={flow.username} platform={flow.platform} avatarUrl={flow.selectedAvatarUrl} onComplete={handleLoadingComplete} />

            <SetupLayout currentStep={flow.currentStep} totalSteps={totalSteps} showBackButton={flow.currentStep > 0} onBack={handleBackClick} backButtonLabel={isPaymentFormSubPage ? "Change plan" : "Back"} isLoggedIn={flow.isLoggedIn} fullWidth={layoutFullWidth}>
                {/* CRITICAL: Don't render step content while any overlay is visible.
                    This prevents BlurTransition from animating underneath the overlay. */}
                {showLoadingOverlay ? <div className="flex-1" /> : <BlurTransition keyValue={flow.currentStep} direction={flow.direction} skipAnimation={flow.currentStep === 0}>
                        {renderStep()}
                    </BlurTransition>}
            </SetupLayout>
        </>;
}